import React from "react";
import PropTypes from "prop-types";
import { Col, Row, Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Card from "views/universal/Components/Card";
import { faTicketAlt, faGift, faTrash, faCommentDots, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Saisie d'un montant en euros. Remplace `react-currency-input-field`, qui
 * refusait le point du pavé numérique (son séparateur décimal étant la virgule)
 * et reformatait le champ à chaque frappe à cause de `decimalScale` et de
 * `intlConfig` — la valeur sautait sous le curseur (retour de Tim, 26/10/2026).
 *
 * Ici le point et la virgule sont acceptés indifféremment, rien n'est reformaté
 * pendant la frappe, et la valeur remontée garde le point en séparateur décimal
 * pour que les consommateurs puissent la multiplier.
 */
const AmountInput = (props) => {
  const { value, onChange, ...rest } = props;

  const display = value === null || value === undefined || Number.isNaN(value)
    ? ''
    : String(value).replace('.', ',');

  const handle = (e) => {
    const typed = e.target.value.replace(/[^0-9.,]/g, '').replace(/\./g, ',');
    const [whole, ...decimals] = typed.split(',');
    const kept = decimals.length > 0
      ? `${whole},${decimals.join('').slice(0, 2)}`
      : whole;

    onChange(kept === '' ? '' : kept.replace(',', '.'));
  };

  return (
    <Form.Control type="text" inputMode="decimal" value={display} onChange={handle} {...rest} />
  );
};

AmountInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

// Regroupe les options par famille (catégories historiques, classifications
// produit, sections de carte, composition), dans l'ordre de l'API.
const groupByCategoryGroup = (items) => {
  const groups = [];
  const index = {};
  (items || []).forEach((c) => {
    const g = c.group || 'Catégories';
    if (index[g] === undefined) {
      index[g] = groups.length;
      groups.push([g, []]);
    }
    groups[index[g]][1].push(c);
  });
  return groups;
};

const OFFER_HELP = (
  <>
    <b>Ce qu'on peut offrir</b> : une catégorie historique (dessert, plat…), une
    classification produit (pain, épicerie, soupe…), une section de la carte, ou
    un ingrédient offert dans une salade ou un plat à composer.
    <br /><br />
    ⚠️ <b>Aucun repli</b> : le client ne pourra utiliser son geste que si un produit
    de la classification est à la carte le jour où il commande. Un avantage SAV
    reste valable jusqu'au 31/12 — la carte peut avoir tourné d'ici là.
    <br /><br />
    <b>Un ingrédient offert</b> : l'ingrédient payant le plus cher de la composition
    (supplément ou premium), <b>dans la limite du montant</b> — au-delà, le client
    paie la différence. Compositions <b>à la carte</b> seulement, pas en formule.
  </>
);

const Variables = (props) => {
  const {
    typeFilter,
    freeCreditAmount,
    note,
    savCategory,
    savCategories,
    savCategoriesGrouped,
    vigilanceLevel,
    vigilanceLevels,
    handleChange,
    addCreditOrderAmount,
    creditOrderAmountPercentage,
    productOptions,
    productCategories,
    stacked = false,
    layout = null,
    sections = null,
  } = props;

  // `sections` permet de n'afficher qu'une partie des cartes : le mode pas à pas
  // de la fenêtre SAV demande le motif à une étape et le geste à une autre.
  const shows = (key) => !sections || sections.includes(key);

  // Les 3 cartes tiennent côte à côte sur la page SAV. Dans la fenêtre SAV
  // (`layout="modal"`), elles étaient empilées sur toute la largeur : on lisait
  // un formulaire interminable. Deux colonnes pour le geste, le motif en dessous
  // sur toute la largeur — c'est lui qui a besoin de place (catégorie, texte,
  // vigilance).
  const isModal = layout === 'modal';
  const cardCols = stacked ? { xs: 12 } : { xs: 12, md: 4 };
  const colCagnotte = isModal ? { xs: 12, lg: 6 } : cardCols;
  const colOffer    = isModal ? { xs: 12, lg: 6 } : cardCols;
  const colReason   = isModal ? { xs: 12 } : cardCols;

  // Familles où le commentaire est indispensable (« Autre », « Erreur individuelle ») :
  // l'API porte l'information avec la sous-catégorie.
  const commentRequired = (savCategoriesGrouped || [])
    .some((family) => (family.subs || []).some((sub) => sub.key === savCategory && sub.commentRequired));

  const PRODUCT_CATEGORIES = productCategories && productCategories.length
    ? productCategories
    : [];

  const usedCategoryKeys = (productOptions || []).map((o) => o.category);
  const availableCategoriesForAdd = PRODUCT_CATEGORIES.filter((c) => !usedCategoryKeys.includes(c.key));
  const allCategoriesUsed = PRODUCT_CATEGORIES.length > 0 && availableCategoriesForAdd.length === 0;

  const addProductOption = () => {
    const first = availableCategoriesForAdd[0];
    if (!first) return;
    handleChange('productOptions', [
      ...(productOptions || []),
      { category: first.key, maxAmount: first.defaultMax }
    ]);
  };

  const updateProductOption = (idx, patch) => {
    const next = (productOptions || []).map((o, i) => i === idx ? { ...o, ...patch } : o);
    handleChange('productOptions', next);
  };

  const removeProductOption = (idx) => {
    handleChange('productOptions', (productOptions || []).filter((_, i) => i !== idx));
  };

  const categoriesAvailableForRow = (idx) => {
    const currentKey = productOptions?.[idx]?.category;
    const usedByOthers = (productOptions || [])
      .filter((_, i) => i !== idx)
      .map((o) => o.category);
    return PRODUCT_CATEGORIES.filter((c) => c.key === currentKey || !usedByOthers.includes(c.key));
  };

  return (
    <Row className="mb-2">
      {shows('cagnotte') && (
      <Col {...colCagnotte} className="mb-2">
        <Card title="Crédit sur cagnotte" description="S'appliquera automatiquement sans choix pour le client." icon={faTicketAlt} colorIcon={'#abd156'}>
          {typeFilter === 'order' && <>
            <Form.Check
              type="checkbox"
              label="Pourcentage du montant de la commande à créditer sur la cagnotte"
              checked={addCreditOrderAmount}
              onChange={(e) => handleChange('addCreditOrderAmount', e.target.checked)}
              className="mt-2"
            />
            {addCreditOrderAmount && (
              <div className="mt-2">
                <Form.Label>Pourcentage (0 à 200 %)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="200"
                  step="1"
                  value={creditOrderAmountPercentage || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    handleChange('creditOrderAmountPercentage', Math.min(Math.max(value, 0), 200));
                  }}
                />
              </div>
            )}
            <hr/>
          </>}
          <p>Montant libre à créditer (€)</p>
          {/* Champ CONTRÔLÉ : en `defaultValue`, la modale rouverte affichait encore
              le montant du SAV précédent alors que l'état avait été vidé. */}
          <AmountInput
            placeholder="Montant libre à créditer"
            value={freeCreditAmount}
            onChange={(value) => handleChange('freeCreditAmount', value)}
          />
        </Card>
      </Col>

      )}

      {shows('offer') && (
      <Col {...colOffer} className="mb-2">
        <Card title="Dédommagement possible" description="Le client aura le choix d'en accepter un parmi les options proposées (facultatif)." icon={faGift} colorIcon={'#fbc02d'}>
          {(productOptions || []).map((opt, idx) => (
            <Row key={idx} className="mb-2 align-items-end">
              <Col xs={5}>
                <Form.Label>
                  Ce qu'on offre{" "}
                  <OverlayTrigger placement="right" overlay={<Tooltip id={`offer-help-${idx}`}>{OFFER_HELP}</Tooltip>}>
                    <FontAwesomeIcon icon={faQuestionCircle} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={opt.category}
                  onChange={(e) => {
                    const cat = PRODUCT_CATEGORIES.find(c => c.key === e.target.value);
                    updateProductOption(idx, {
                      category: e.target.value,
                      maxAmount: cat ? cat.defaultMax : opt.maxAmount,
                    });
                  }}
                >
                  {groupByCategoryGroup(categoriesAvailableForRow(idx)).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Control>
              </Col>
              <Col xs={5}>
                <Form.Label>Dans la limite de (€)</Form.Label>
                <AmountInput
                  value={opt.maxAmount}
                  onChange={(value) => updateProductOption(idx, { maxAmount: value })}
                />
              </Col>
              <Col xs={2}>
                <Button variant="outline-danger" size="sm" onClick={() => removeProductOption(idx)}>
                  <FontAwesomeIcon icon={faTrash}/>
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            variant="warning"
            onClick={addProductOption}
            className="mt-3 w-100 font-weight-bold"
            disabled={allCategoriesUsed}
          >
            {allCategoriesUsed ? 'Toutes les catégories sont déjà proposées' : '+ Ajouter une option de compensation'}
          </Button>
        </Card>
      </Col>

      )}

      {shows('reason') && (
      <Col {...colReason} className="mb-2">
        {/* Dans la fenêtre SAV, le motif est devenu interne : ce que le client lit
            est le message de la dernière étape. Sur la page de crédit de masse, il
            part bien dans le mail — d'où les deux libellés. */}
        <Card
          title="Motif du SAV"
          description={isModal
            ? "Interne : statistiques, ticket et trace. Le client lit le message de la dernière étape. Obligatoire."
            : "Visible dans le mail envoyé au client. Obligatoire."}
          icon={faCommentDots}
          colorIcon={'#5b9bd5'}
        >
          <Form.Label>
            Catégorie du SAV (interne, obligatoire){" "}
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="sav-category-tooltip">
                  Classification interne du problème (oubli / inversion d'ingrédient,
                  de sauce, de sac, de caisson, de tournée, de commande, de vérif,
                  qualité, retard, annulation…). Jamais visible du client.
                  Sert aux stats pour identifier d'où viennent les erreurs
                  et suivre leur évolution. En cas de doute, choisir « Inclassable ».
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} style={{ cursor: 'help' }} />
            </OverlayTrigger>
          </Form.Label>
          {/* Taxonomie à deux niveaux : on choisit une famille, puis la sous-catégorie.
              Repli sur la liste à plat tant que l'API n'a pas renvoyé le groupement. */}
          <Form.Control
            as="select"
            value={savCategory || ''}
            onChange={(e) => handleChange('savCategory', e.target.value)}
            className="mb-3"
          >
            <option value="">— Choisir une catégorie —</option>
            {(savCategoriesGrouped || []).length > 0
              ? (savCategoriesGrouped || []).map((family) => (
                  <optgroup key={family.key} label={family.label}>
                    {(family.subs || []).map((sub) => (
                      <option key={sub.key} value={sub.key}>{sub.label}</option>
                    ))}
                  </optgroup>
                ))
              : Object.entries(savCategories || {}).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
          </Form.Control>
          {commentRequired && (
            <p className="text-warning" style={{ fontSize: '0.85rem', marginTop: '-0.5rem' }}>
              Cette catégorie ne dit rien à elle seule : décrivez le problème dans le motif ci-dessous.
            </p>
          )}
          <Form.Label>Motif</Form.Label>
          {/* Champ CONTRÔLÉ : en `defaultValue`, le motif du SAV précédent restait
              écrit à la réouverture de la fenêtre alors que l'état avait été vidé
              (retour de Tim, 26/10/2026). */}
          <Form.Control
            as="textarea"
            rows={4}
            value={note || ''}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="Ex : retard de livraison, produit manquant, etc."
          />
          {isModal && (
            <Form.Text muted>
              Ce texte ne part pas au client tel quel : il sert aux statistiques, au ticket
              Freshdesk et à la trace. Il n'est repris dans le mail que si vous n'écrivez
              aucun message à la dernière étape.
            </Form.Text>
          )}
          <Form.Label className="mt-3">
            Vigilance client (optionnel){" "}
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="sav-vigilance-tooltip">
                  Pose une pastille de couleur à côté du nom du client sur les fiches
                  de production : les équipiers redoublent d'attention en préparant
                  ses prochaines commandes. Le niveau disparaît automatiquement après
                  1 (vert), 2 (orange) ou 10 (rouge) commandes livrées sans problème.
                  Ne rétrograde jamais un niveau déjà posé. Invisible du client.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} style={{ cursor: 'help' }} />
            </OverlayTrigger>
          </Form.Label>
          <Form.Control
            as="select"
            value={vigilanceLevel || ''}
            onChange={(e) => handleChange('vigilanceLevel', e.target.value)}
          >
            <option value="">Aucun changement</option>
            {Object.entries(vigilanceLevels || {}).map(([level, label]) => (
              <option key={level} value={level}>{label}</option>
            ))}
          </Form.Control>
        </Card>
      </Col>
      )}
    </Row>
  );
};

Variables.propTypes = {
  typeFilter: PropTypes.string,
  freeCreditAmount: PropTypes.number,
  note: PropTypes.string,
  savCategory: PropTypes.string,
  savCategories: PropTypes.object,
  savCategoriesGrouped: PropTypes.array,
  vigilanceLevel: PropTypes.string,
  vigilanceLevels: PropTypes.object,
  handleChange: PropTypes.func,
  addCreditOrderAmount: PropTypes.bool,
  creditOrderAmountPercentage: PropTypes.number,
  productOptions: PropTypes.array,
  stacked: PropTypes.bool,
  layout: PropTypes.string,
};

export default Variables;
